import * as Sentry from "@sentry/react";

type SSEEventHandler = (data: any) => void;

interface SSEClientOptions {
  url: string;
  onEvent?: SSEEventHandler;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onReconnectExhausted?: () => void;
}

class SSEConnectionManager {
  private eventSource: EventSource | null = null;
  private handlers = new Map<string, Set<SSEEventHandler>>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private url: string;
  private connected = false;
  private onConnectCallback?: () => void;
  private onDisconnectCallback?: () => void;
  private onReconnectExhaustedCallback?: () => void;

  constructor(options: SSEClientOptions) {
    this.url = options.url;
    this.onConnectCallback = options.onConnect;
    this.onDisconnectCallback = options.onDisconnect;
    this.onReconnectExhaustedCallback = options.onReconnectExhausted;
    if (options.onEvent) {
      this.on("*", options.onEvent);
    }
  }

  connect() {
    if (this.eventSource) return;

    try {
      this.eventSource = new EventSource(this.url, { withCredentials: true });

      this.eventSource.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.onConnectCallback?.();
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "connected") return;
          if (data.eventType) {
            this.emit(data.eventType, data);
          }
          this.emit("*", data);
        } catch (err) {
          console.error("[sse] Failed to parse SSE message:", err);
          Sentry.captureException(err, { extra: { context: "sse-message-parse", raw: event.data?.slice(0, 200) } });
        }
      };

      this.eventSource.onerror = () => {
        this.disconnect(false);
        this.scheduleReconnect();
      };

      const knownEvents = [
        "payment_received",
        "invoice_created",
        "invoice_approved",
        "invoice_sent",
        "invoice_disputed",
        "invoice_voided",
        "bill_created",
        "bill_approved",
        "bill_overdue",
        "invoice_overdue",
        "customer_created",
        "vendor_created",
        "dispute_opened",
        "dispute_status_changed",
        "dispute_resolved",
        "demo_reply_pending",
        "demo_reply_resolved",
      ];
      for (const eventName of knownEvents) {
        this.eventSource.addEventListener(eventName, (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            this.emit(eventName, data);
            this.emit("*", data);
          } catch (err) {
            console.error("[sse] Failed to parse SSE event:", err);
            Sentry.captureException(err, { extra: { context: "sse-event-parse", eventName, raw: event.data?.slice(0, 200) } });
          }
        });
      }
    } catch (err) {
      console.error("[sse] Failed to connect to SSE endpoint:", err);
      Sentry.captureException(err, { extra: { context: "sse-connect" } });
      this.scheduleReconnect();
    }
  }

  disconnect(manual = true) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.connected) {
      this.connected = false;
      this.onDisconnectCallback?.();
    }
    if (manual) {
      this.reconnectAttempts = this.maxReconnectAttempts;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.onReconnectExhaustedCallback?.();
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  on(eventName: string, handler: SSEEventHandler) {
    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Set());
    }
    this.handlers.get(eventName)!.add(handler);
    return () => {
      this.handlers.get(eventName)?.delete(handler);
    };
  }

  off(eventName: string, handler: SSEEventHandler) {
    this.handlers.get(eventName)?.delete(handler);
  }

  private emit(eventName: string, data: any) {
    this.handlers.get(eventName)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.error("[sse] SSE event handler threw an error:", err);
        Sentry.captureException(err, { extra: { context: "sse-event-handler", eventName } });
      }
    });
  }

  isConnected() {
    return this.connected;
  }

  setReconnectExhaustedCallback(cb: () => void) {
    this.onReconnectExhaustedCallback = cb;
  }
}

let instance: SSEConnectionManager | null = null;

export function setSSEReconnectExhaustedHandler(cb: () => void) {
  if (!instance) {
    instance = new SSEConnectionManager({ url: "/api/events/stream" });
  }
  instance.setReconnectExhaustedCallback(cb);
}

export function getSSEClient(): SSEConnectionManager {
  if (!instance) {
    instance = new SSEConnectionManager({ url: "/api/events/stream" });
  }
  return instance;
}

export function destroySSEClient() {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
}

export { SSEConnectionManager };
